import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { sha256Hex } from '../../lib/hash';
import { useAuth } from '../../lib/auth';
import type { DefectReport } from '../../lib/types';
import { useApiQuery, useCursorList } from '../../lib/useApi';
import { BackLink, EmptyState, ErrorNote, Field, LoadMoreButton, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const CATEGORIES = ['POTHOLE', 'DRAIN', 'STREETLIGHT', 'SIGNAGE'] as const;
const STATES = ['REPORTED', 'TRIAGED', 'SCHEDULED', 'FIXED', 'CLOSED'] as const;

export function DefectReports() {
  const { persona } = useAuth();
  const isStaff = (persona?.roles ?? []).some((r) => ['WORKS_OFFICER', 'ROADS_ENGINEER'].includes(r));
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [q, setQ] = useState('');
  const [showNew, setShowNew] = useState(false);

  const basePath = useMemo(() => {
    const params = new URLSearchParams();
    params.set('limit', '25');
    if (status) params.set('status', status);
    if (category) params.set('category', category);
    if (q.trim()) params.set('q', q.trim());
    return `/api/v1/m20/defects?${params.toString()}`;
  }, [status, category, q]);
  const list = useCursorList<DefectReport>(basePath);

  return (
    <div>
      <PageHeader
        title="Defect Reports"
        subtitle="Public status map (reporter identity redacted unless staff/self) with automatic dedupe-clustering by category + proximity."
        actions={<button className="btn-primary" onClick={() => setShowNew(true)}>+ Log defect</button>}
      />

      <div className="card mb-4 flex flex-wrap items-end gap-3 p-4">
        <Field label="Status" className="w-44">
          <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Category" className="w-44">
          <select className="field-input" value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">All categories</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Search" className="min-w-[200px] flex-1">
          <input className="field-input" placeholder="Ref no or description" value={q} onChange={(e) => setQ(e.target.value)} />
        </Field>
      </div>

      {list.error ? <div className="mb-4"><ErrorNote message={list.error} /></div> : null}

      <div className="card overflow-x-auto">
        <table className="w-full min-w-[680px] border-collapse">
          <thead>
            <tr>
              <th className="table-head">Ref no</th>
              <th className="table-head">Category</th>
              <th className="table-head">Location</th>
              <th className="table-head">Duplicates</th>
              <th className="table-head">Status</th>
              <th className="table-head">Reported</th>
            </tr>
          </thead>
          <tbody>
            {list.items.map((d) => (
              <tr key={d.id} className="table-row-link" onClick={() => navigate(`/infrastructure/defects/${d.id}`)}>
                <td className="table-cell font-semibold text-slate-800">{d.refNo}</td>
                <td className="table-cell text-xs">{d.category}</td>
                <td className="table-cell text-xs text-slate-500">{d.district ?? `${d.gpsLat?.toFixed(4)}, ${d.gpsLng?.toFixed(4)}`}</td>
                <td className="table-cell tabular-nums">{d.duplicateCount > 0 ? `+${d.duplicateCount}` : '—'}</td>
                <td className="table-cell"><StatusBadge status={d.state} /></td>
                <td className="table-cell text-xs text-slate-500">{formatDate(d.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.loading ? <Spinner label="Loading defect reports…" /> : null}
        {!list.loading && list.items.length === 0 ? <EmptyState title="No defect reports found" /> : null}
      </div>
      <LoadMoreButton nextCursor={list.nextCursor} loading={list.loading} onClick={list.loadMore} />
      {!isStaff ? <p className="mt-3 text-xs text-slate-400">Triage/schedule/fix/close actions require WORKS_OFFICER or ROADS_ENGINEER.</p> : null}

      {showNew ? (
        <NewDefectModal onClose={() => setShowNew(false)} onCreated={(d) => { setShowNew(false); navigate(`/infrastructure/defects/${d.id}`); }} />
      ) : null}
    </div>
  );
}

function NewDefectModal({ onClose, onCreated }: { onClose: () => void; onCreated: (d: DefectReport) => void }) {
  const [category, setCategory] = useState<(typeof CATEGORIES)[number]>('POTHOLE');
  const [description, setDescription] = useState('');
  const [gpsLat, setGpsLat] = useState('');
  const [gpsLng, setGpsLng] = useState('');
  const [district, setDistrict] = useState('');
  const [reporterContact, setReporterContact] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const photoDocRef = `defect-report-${Date.now()}`;
      const sha256 = await sha256Hex(photoDocRef + description);
      const created = await api.post<DefectReport>('/api/v1/m20/defects', {
        category,
        description: description || undefined,
        gpsLat: Number(gpsLat),
        gpsLng: Number(gpsLng),
        district: district || undefined,
        photoDocRef,
        sha256,
        reporterContact: reporterContact || undefined,
      });
      onCreated(created);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not log defect');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Log a defect report" onClose={onClose} wide>
      <p className="mb-3 text-xs text-slate-500">
        POST /api/v1/m20/defects — the citizen 3-tap flow's real staff-side equivalent (e.g. a phone-in report at the
        public-works call centre); dedupe-clusters onto a nearby open report of the same category automatically.
      </p>
      {error ? <div className="mb-3"><ErrorNote message={error} /></div> : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Category">
          <select className="field-input" value={category} onChange={(e) => setCategory(e.target.value as typeof category)}>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="District (optional — resolved from GPS if omitted)">
          <input className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)} />
        </Field>
        <Field label="GPS latitude">
          <input className="field-input" type="number" step="0.000001" value={gpsLat} onChange={(e) => setGpsLat(e.target.value)} placeholder="-4.6796" />
        </Field>
        <Field label="GPS longitude">
          <input className="field-input" type="number" step="0.000001" value={gpsLng} onChange={(e) => setGpsLng(e.target.value)} placeholder="55.4920" />
        </Field>
        <Field label="Reporter contact (optional)">
          <input className="field-input" value={reporterContact} onChange={(e) => setReporterContact(e.target.value)} />
        </Field>
        <Field label="Description" className="sm:col-span-2">
          <textarea className="field-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={busy || !gpsLat || !gpsLng} onClick={submit}>
          {busy ? 'Logging…' : 'Log defect'}
        </button>
      </div>
    </Modal>
  );
}

export function DefectDetail() {
  const { id = '' } = useParams();
  const { persona } = useAuth();
  const isStaff = (persona?.roles ?? []).some((r) => ['WORKS_OFFICER', 'ROADS_ENGINEER'].includes(r));
  const defectQ = useApiQuery<DefectReport>(`/api/v1/m20/defects/${id}`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assignedTo, setAssignedTo] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [fixNotes, setFixNotes] = useState('');
  const [closeReason, setCloseReason] = useState('');

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      defectQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  if (defectQ.loading) return <Spinner label="Loading defect report…" />;
  if (defectQ.error || !defectQ.data) return <ErrorNote message={defectQ.error ?? 'Defect not found'} />;
  const d = defectQ.data;

  return (
    <div>
      <BackLink to="/infrastructure/defects" />
      <PageHeader title={d.refNo} subtitle={`${d.category} · ${d.district ?? 'GPS ' + d.gpsLat + ', ' + d.gpsLng}`} actions={<StatusBadge status={d.state} />} />
      {error ? <div className="mb-4"><ErrorNote message={error} /></div> : null}

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="card p-5 lg:col-span-2">
          <h2 className="mb-2 text-sm font-bold text-slate-700">Report</h2>
          <p className="text-sm text-slate-600">{d.description ?? 'No description provided.'}</p>
          {d.duplicateCount > 0 ? <p className="mt-2 text-xs font-semibold text-amber-600">{d.duplicateCount} other citizen(s) reported the same issue.</p> : null}
          {d.reporterId ? <p className="mt-2 text-xs text-slate-500">Reporter: {d.reporterId} {d.reporterContact ? `(${d.reporterContact})` : ''}</p> : null}
          {d.scheduledDate ? <p className="mt-2 text-xs text-slate-500">Scheduled for {formatDate(d.scheduledDate)}</p> : null}
          {d.fixedAt ? <p className="mt-2 text-xs text-emerald-600">Fixed {formatDateTime(d.fixedAt)} — {d.fixedNotes}</p> : null}
          {d.closedAt ? <p className="mt-2 text-xs text-slate-500">Closed {formatDateTime(d.closedAt)} — {d.closedReason}</p> : null}
        </div>

        <div className="card p-5">
          <h2 className="mb-3 text-sm font-bold text-slate-700">Actions</h2>
          {!isStaff ? <p className="text-xs text-slate-400">Requires WORKS_OFFICER or ROADS_ENGINEER.</p> : null}
          {isStaff && d.state === 'REPORTED' ? (
            <div className="mb-3 space-y-2">
              <Field label="Assign to"><input className="field-input" value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} /></Field>
              <button className="btn-primary w-full" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m20/defects/${id}/triage`, { assignedTo: assignedTo || undefined }))}>
                Triage
              </button>
            </div>
          ) : null}
          {isStaff && d.state === 'TRIAGED' ? (
            <div className="mb-3 space-y-2 border-t border-slate-100 pt-3">
              <Field label="Scheduled date"><input className="field-input" type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} /></Field>
              <button className="btn-primary w-full" disabled={busy || !scheduledDate} onClick={() => act(() => api.post(`/api/v1/m20/defects/${id}/schedule`, { scheduledDate }))}>
                Schedule repair
              </button>
            </div>
          ) : null}
          {isStaff && ['TRIAGED', 'SCHEDULED'].includes(d.state) ? (
            <div className="mb-3 space-y-2 border-t border-slate-100 pt-3">
              <Field label="Fix notes"><textarea className="field-input" rows={2} value={fixNotes} onChange={(e) => setFixNotes(e.target.value)} /></Field>
              <button
                className="btn-success w-full"
                disabled={busy}
                onClick={() => act(async () => {
                  const photoDocRef = `defect-fixed-${d.refNo}`;
                  const sha256 = await sha256Hex(photoDocRef + fixNotes);
                  await api.post(`/api/v1/m20/defects/${id}/fix`, { photoDocRef, sha256, notes: fixNotes || undefined });
                })}
              >
                Mark fixed
              </button>
            </div>
          ) : null}
          {isStaff && d.state !== 'CLOSED' ? (
            <div className="space-y-2 border-t border-slate-100 pt-3">
              <Field label="Close reason (optional)"><input className="field-input" value={closeReason} onChange={(e) => setCloseReason(e.target.value)} /></Field>
              <button className="btn-secondary w-full" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m20/defects/${id}/close`, { reason: closeReason || undefined }))}>
                Close report
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
